import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const DripCard = styled(motion.div)`
  display: grid;
  grid-template-rows: 3fr 1fr;
  border-radius: 20px;
  padding: 24px;
  width: 80%;
  height: 100%;
  margin: 0 auto;
  gap: 24px;
  backdrop-filter: blur(10px);

  @media (max-width: 768px) {
    padding: 16px;
    width: 100%;
    height: 100%;
    gap: 0;
  }
`;

const BackButton = styled(motion.button)`
  position: absolute;
  top: 15px;
  left: 15px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  font-size: 1.2rem;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  z-index: 10;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    top: 15px;
    left: 15px;
    width: 35px;
    height: 35px;
    font-size: 1.1rem;
  }
`;

const CommentsSection = styled.div`
  background: rgba(41, 41, 41, 0.5);
  backdrop-filter: blur(10px);
  box-shadow: rgba(0, 0, 0, 0.3) 0px 8px 32px;
  width: 100%;
  border-radius: 15px;
  padding: 15px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const CommentsTitle = styled.h2`
  color: #fff;
  font-size: 1.2rem;
  font-weight: 500;
  margin-top: 10px;
  margin-bottom: 12px;
  text-align: center;
  border-bottom: 2px solid rgba(255, 255, 255, 0.2);
  padding-bottom: 8px;
  padding-left: 80px;
  padding-right: 80px;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
}
`;

const CommentsList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
  }
`;

const CommentItem = styled.div`
  background: rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 8px;
  border-left: 3px solid var(--primary-color);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    transform: translateX(3px);
  }
`;

const CommentAuthor = styled.div`
  font-weight: 600;
  color: var(--primary-color);
  font-size: 0.8rem;
  margin-bottom: 4px;
`;

const CommentText = styled.div`
  color: #fff;
  font-size: 0.85rem;
  line-height: 1.3;
  margin-bottom: 6px;
  word-break: keep-all;
`;

const CommentStats = styled.div`
  display: flex;
  gap: 12px;
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.7);

  span {
    display: flex;
    align-items: center;
    gap: 2px;
  }
`;

const DripSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  width: 100%;
`;

const DripText = styled(motion.h2)`
  text-align: center;
  font-size: calc(1.8vh + 0.8rem);
  margin-bottom: 16px;
  color: #fff;
  font-weight: normal;

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  
  @media (max-width: 768px) {
    gap: 5px;
    margin-top: 0px;
    margin-bottom: 5px;
  }
`;

const InfoText = styled(motion.div)`
  color: rgb(255, 182, 193);
  font-weight: 600;
  font-size: 0.85rem;
  opacity: 1;
  transition: opacity 0.3s ease;
  font-weight: 300;
  white-space: nowrap;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const SubmitButton = styled(motion.button)`
  background: transparent;
  border-radius: 15px;
  padding: 8px 30px;
  height: 45px;
  font-size: 1.2rem;
  width: 60%;
  color: rgba(255, 255, 255, 0.83);
  text-transform: none;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  border: 2px solid rgba(136, 136, 136, 0.5);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
    
  @media (max-width: 768px) {
    height: 45px;
    padding: 8px 20px;
    font-size: 1.2rem;
    width: 80%;
  }

  &:hover {
    background: var(--primary-color);
    color: rgb(255, 255, 255);
    border: 2px solid rgba(255, 255, 255, 0.77);
  }
  
  &:disabled {
    border: 2px solid rgba(136, 136, 136, 0.5);
    color: rgba(136, 136, 136, 0.5);
  }
`;

const DripSubmit = ({ 
  selectedVideo, 
  comments, 
  commentsLoading, 
  onSubmit,
  onBack
}) => {
  if (!selectedVideo) return null;

  return (
    <DripCard
      key="drip"
      initial={{ 
        opacity: 0, 
        y: 50, 
        scale: 0.9,
        rotateX: -10
      }}
      animate={{ 
        opacity: 1, 
        y: 0, 
        scale: 1,
        rotateX: 0
      }}
      transition={{ 
        duration: 0.8,
        type: "spring",
        stiffness: 100,
        damping: 15
      }}
    >
      {/* 댓글 영역 */}
      <CommentsSection
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <BackButton
          onClick={onBack}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          ‹
        </BackButton>
        <CommentsTitle>💬 인기 댓글 TOP 20</CommentsTitle>
        <CommentsList>
          {commentsLoading ? (
            <div style={{ textAlign: 'center', color: 'rgba(255, 255, 255, 0.6)', padding: '20px' }}>
              댓글을 불러오는 중...
            </div>
          ) : comments.length > 0 ? (
            comments.map((comment) => (
              <CommentItem key={comment.id}>
                <CommentAuthor>{comment.author}</CommentAuthor>
                <CommentText>{comment.text}</CommentText>
                <CommentStats>
                  <span>👍 {comment.likes.toLocaleString()}</span>
                  <span>💬 {comment.replies}</span>
                </CommentStats>
              </CommentItem>
            ))
          ) : (
            <div style={{ textAlign: 'center', color: 'rgba(255, 255, 255, 0.6)', padding: '20px' }}>
              댓글이 없습니다.
            </div>
          )}
        </CommentsList>
      </CommentsSection>

      {/* 드립 작성 영역 */}
      <DripSection>
        <DripText
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ 
            duration: 0.6,
            delay: 0.3,
            ease: "easeOut"
          }}
        >
          드립 하나로 인생 갈린다.
        </DripText>
        
        <ButtonContainer>
          <InfoText
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ 
              opacity: 1, 
              y: 0, 
              scale: 1 
            }}
            transition={{ 
              duration: 0.5,
              delay: 0.7,
              ease: "easeOut"
            }}
          >
            * 원하는 경품에 응모해보세요!
          </InfoText>
          <SubmitButton
            onClick={onSubmit}
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ 
              opacity: 1, 
              y: 0, 
              scale: 1 
            }}
            transition={{ 
              duration: 0.5,
              delay: 0.8,
              ease: "easeOut"
            }}
            whileHover={{
              scale: 1.05,
              y: -3,
              transition: { duration: 0.2 }
            }}
            whileTap={{
              scale: 0.95,
              transition: { duration: 0.1 }
            }}
          >
            드립치고 응모하기
          </SubmitButton>
        </ButtonContainer>
      </DripSection>
    </DripCard>
  );
};

export default DripSubmit; 